import React, { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card, Pill } from "../../components/common/Primitives";
import { Button } from "../../components/ui/button";
import {
  Loader2, TrendingUp, TrendingDown, GitCompare, Users,
  Sparkles, ChevronRight, RefreshCw, X,
} from "lucide-react";
import api, { formatApiError } from "../../lib/api";

const inr = (v) => (v == null ? "—" : "₹" + Number(v).toLocaleString("en-IN"));

const driftPill = (v) => {
  if (v == null) return <Pill tone="muted">— insufficient data</Pill>;
  if (v <= 20) return <Pill tone="success" data-testid="drift-pill-low">Aligned · {v}%</Pill>;
  if (v <= 45) return <Pill tone="warn" data-testid="drift-pill-mid">Moderate · {v}%</Pill>;
  return <Pill tone="danger" data-testid="drift-pill-high">Divergent · {v}%</Pill>;
};

const FunnelStrip = ({ funnel = {} }) => {
  const order = [
    ["view", "Views"],
    ["save", "Saves"],
    ["interest", "Interests"],
    ["deal_room", "Deal Rooms"],
    ["rejection", "Rejections"],
  ];
  return (
    <div className="flex flex-wrap gap-2">
      {order.map(([k, label]) => (
        <div
          key={k}
          data-testid={`funnel-${k}`}
          className="border border-navy/[0.08] px-3 py-1.5 rounded-md text-xs bg-white/40"
        >
          <span className="uppercase font-mono text-[10px] tracking-wider text-ink-muted mr-1.5">
            {label}
          </span>
          <span className="font-mono text-ink font-semibold">
            {funnel[k] || 0}
          </span>
        </div>
      ))}
    </div>
  );
};

const DimensionRow = ({ dim, data }) => {
  const drift = data?.drift;
  return (
    <div
      className="border border-navy/[0.08] rounded-md p-3 bg-white/40"
      data-testid={`dim-row-${dim}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="font-heading text-ink text-sm capitalize">
          {dim.replace(/_/g, " ")}
        </div>
        {drift == null ? (
          <span className="text-[10px] font-mono text-ink-muted">no data</span>
        ) : drift <= 20 ? (
          <Pill tone="success">{drift}% drift</Pill>
        ) : drift <= 45 ? (
          <Pill tone="warn">{drift}% drift</Pill>
        ) : (
          <Pill tone="danger">{drift}% drift</Pill>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 text-xs">
        <div>
          <div className="uppercase font-mono text-[10px] text-ink-muted mb-1">
            Declared
          </div>
          <div className="flex flex-wrap gap-1">
            {(data.declared || []).length ? (
              data.declared.map((d) => (
                <span
                  key={d}
                  className="px-2 py-0.5 rounded bg-navy/[0.05] text-ink text-[11px]"
                >
                  {d}
                </span>
              ))
            ) : (
              <span className="italic text-ink-muted">Not set</span>
            )}
          </div>
        </div>
        <div>
          <div className="uppercase font-mono text-[10px] text-ink-muted mb-1">
            Observed
          </div>
          <div className="flex flex-wrap gap-1">
            {(data.observed || []).length ? (
              data.observed.slice(0, 4).map((o, idx) => (
                <span
                  key={idx}
                  className={
                    "px-2 py-0.5 rounded text-[11px] " +
                    ((data.matched || [])
                      .map((s) => String(s).toLowerCase())
                      .includes(String(o.value).toLowerCase())
                      ? "bg-emerald-100 text-emerald-800"
                      : "bg-amber-100 text-amber-800")
                  }
                >
                  {o.value} · {o.share}%
                </span>
              ))
            ) : (
              <span className="italic text-ink-muted">No engagement</span>
            )}
          </div>
        </div>
      </div>
      {(data.missing || []).length > 0 && (
        <div className="mt-2 text-[11px] text-ink-muted">
          <span className="font-mono uppercase text-[9px] tracking-wider mr-1">
            Missing
          </span>
          {data.missing.join(", ")}
        </div>
      )}
      {(data.unexpected || []).length > 0 && (
        <div className="mt-1 text-[11px] text-ink-muted">
          <span className="font-mono uppercase text-[9px] tracking-wider mr-1">
            Unexpected
          </span>
          {data.unexpected.join(", ")}
        </div>
      )}
    </div>
  );
};

const DetailDrawer = ({ investorId, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState(null);
  const [insightLoading, setInsightLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get(`/admin/mandate-graph/${investorId}`)
      .then((r) => {
        if (!cancelled) setDetail(r.data);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [investorId]);

  const runInsights = async (force = false) => {
    setInsightLoading(true);
    try {
      const { data } = await api.post(
        `/admin/mandate-graph/${investorId}/insights${force ? "?force=true" : ""}`
      );
      setInsights(data);
      toast.success(`Insights ready (${data.source})`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setInsightLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="mandate-detail-drawer">
      <div
        className="absolute inset-0 bg-navy/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative ml-auto w-full max-w-3xl h-full bg-white shadow-2xl overflow-y-auto">
        <div className="sticky top-0 z-10 bg-white border-b border-navy/[0.08] px-6 py-4 flex items-center justify-between">
          <div>
            <div className="overline text-ink-muted">Investor Mandate Graph</div>
            <div className="font-heading text-2xl text-ink">
              {detail?.investor?.investor_name ||
                detail?.investor?.firm_name ||
                investorId}
            </div>
            <div className="text-xs text-ink-muted mt-0.5">
              {detail?.investor?.investor_type} · {detail?.investor?.status}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-md hover:bg-navy/[0.05]"
            data-testid="mandate-drawer-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {loading && (
            <div className="text-ink-muted">
              <Loader2 className="animate-spin inline mr-2" size={14} />
              Loading mandate graph…
            </div>
          )}
          {detail && (
            <>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="overline">Overall Drift</span>
                  {driftPill(detail.drift?.overall_drift)}
                </div>
                <div className="text-xs font-mono text-ink-muted">
                  {detail.actual?.total_events || 0} events ·{" "}
                  {detail.actual?.unique_startups || 0} unique startups
                </div>
              </div>

              <div>
                <div className="overline mb-2">Engagement Funnel</div>
                <FunnelStrip funnel={detail.actual?.funnel} />
              </div>

              <div>
                <div className="overline mb-2">Mandate Dimensions</div>
                <div className="grid grid-cols-1 gap-3">
                  {Object.entries(detail.drift?.by_dimension || {}).map(
                    ([dim, data]) => (
                      <DimensionRow key={dim} dim={dim} data={data} />
                    )
                  )}
                </div>
              </div>

              <div className="border border-gold/40 rounded-md p-4 bg-gradient-to-br from-white to-gold/[0.05]">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Sparkles size={16} className="text-gold" />
                    <div className="overline text-ink">
                      AI Analyst Note (Claude Sonnet 4.5)
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!insights && (
                      <Button
                        size="sm"
                        onClick={() => runInsights(false)}
                        disabled={insightLoading}
                        data-testid="mandate-generate-insights"
                      >
                        {insightLoading ? (
                          <Loader2 className="animate-spin" size={14} />
                        ) : (
                          "Generate Insights"
                        )}
                      </Button>
                    )}
                    {insights && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => runInsights(true)}
                        disabled={insightLoading}
                        data-testid="mandate-regenerate-insights"
                      >
                        {insightLoading ? (
                          <Loader2 className="animate-spin" size={14} />
                        ) : (
                          <>
                            <RefreshCw size={12} className="mr-1" />
                            Regenerate
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
                {insights ? (
                  <div
                    className="text-sm text-ink whitespace-pre-line leading-relaxed"
                    data-testid="mandate-insights-body"
                  >
                    {insights.narrative}
                    <div className="mt-2 text-[10px] font-mono uppercase text-ink-muted tracking-wider">
                      Source: {insights.source}
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-ink-muted italic">
                    Generate a narrative to see declared-vs-observed analysis
                    and recommended action.
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default function AdminMandateGraph() {
  const [rows, setRows] = useState(null);
  const [openInvestor, setOpenInvestor] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = () => {
    api
      .get("/admin/mandate-graph")
      .then((r) => setRows(r.data.investors || []))
      .catch((e) => toast.error(formatApiError(e)));
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = (rows || []).filter((r) => {
    if (filter === "divergent") return (r.overall_drift ?? 0) > 45;
    if (filter === "aligned") return r.overall_drift != null && r.overall_drift <= 20;
    if (filter === "active") return (r.total_events || 0) >= 5;
    return true;
  });

  const summary = (rows || []).reduce(
    (acc, r) => {
      if (r.overall_drift == null) acc.no_data++;
      else if (r.overall_drift > 45) acc.divergent++;
      else if (r.overall_drift <= 20) acc.aligned++;
      else acc.moderate++;
      return acc;
    },
    { aligned: 0, moderate: 0, divergent: 0, no_data: 0 }
  );

  return (
    <DashboardShell
      title="Mandate Graph"
      subtitle="ADMIN · DECLARED vs BEHAVIORAL"
    >
      <Toaster richColors position="top-right" />

      <Card testId="mandate-explainer" className="mb-6">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-md bg-navy/[0.05]">
            <GitCompare size={18} className="text-navy" />
          </div>
          <div className="flex-1">
            <div className="overline text-ink-muted">How it works</div>
            <div className="text-sm text-ink mt-1 leading-relaxed">
              We compare each investor&apos;s <b>declared mandate</b> (sectors,
              stages, ticket size, geography, business model, tenor, revenue
              threshold) against their <b>actual behavior</b> across views,
              saves, interests, deal rooms and rejections. Drift &gt; 45% flags a
              mismatch worth reviewing.
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          ["Aligned", summary.aligned, "success"],
          ["Moderate", summary.moderate, "warn"],
          ["Divergent", summary.divergent, "danger"],
          ["Insufficient data", summary.no_data, "muted"],
        ].map(([label, val, tone]) => (
          <div
            key={label}
            className="border border-navy/[0.08] rounded-md p-4 bg-white/40"
            data-testid={`mandate-summary-${label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <div className="overline text-ink-muted">{label}</div>
            <div className="font-heading text-3xl text-ink mt-1">{val}</div>
            <div className="mt-1">
              <Pill tone={tone}>
                {label === "Aligned" && "≤ 20% drift"}
                {label === "Moderate" && "21–45%"}
                {label === "Divergent" && "> 45%"}
                {label === "Insufficient data" && "< 3 events"}
              </Pill>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        {[
          ["all", "All"],
          ["active", "Active (5+ events)"],
          ["divergent", "Divergent"],
          ["aligned", "Aligned"],
        ].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            data-testid={`mandate-filter-${key}`}
            className={
              "px-3 py-1.5 rounded-md text-xs uppercase font-mono tracking-wider transition " +
              (filter === key
                ? "bg-navy text-white"
                : "bg-white text-ink border border-navy/[0.1] hover:bg-navy/[0.05]")
            }
          >
            {label}
          </button>
        ))}
        <div className="ml-auto text-xs text-ink-muted font-mono">
          {filtered.length} / {(rows || []).length} investors
        </div>
      </div>

      <Card testId="mandate-table">
        {!rows && (
          <div className="text-ink-muted">
            <Loader2 className="animate-spin inline mr-2" size={14} />
            Loading investors…
          </div>
        )}
        {rows && filtered.length === 0 && (
          <div className="text-ink-muted italic text-sm py-6 text-center">
            No investors match this filter.
          </div>
        )}
        {rows && filtered.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="mandate-investors-table">
              <thead>
                <tr className="text-[10px] font-mono uppercase text-ink-muted tracking-wider">
                  <th className="text-left py-2 pl-2">Investor</th>
                  <th className="text-left py-2">Type</th>
                  <th className="text-right py-2">Events</th>
                  <th className="text-right py-2">Uniq. Startups</th>
                  <th className="text-left py-2 pl-2">Funnel</th>
                  <th className="text-right py-2">Drift</th>
                  <th className="text-right py-2 pr-2"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr
                    key={r.investor_id}
                    className="border-t border-navy/[0.06] hover:bg-navy/[0.02] cursor-pointer"
                    onClick={() => setOpenInvestor(r.investor_id)}
                    data-testid={`mandate-row-${r.investor_id}`}
                  >
                    <td className="py-3 pl-2">
                      <div className="font-heading text-ink">
                        {r.investor_name || r.investor_id}
                      </div>
                    </td>
                    <td className="py-3 text-ink-muted text-xs">
                      {r.investor_type || "—"}
                    </td>
                    <td className="py-3 text-right font-mono text-ink">
                      {r.total_events || 0}
                    </td>
                    <td className="py-3 text-right font-mono text-ink">
                      {r.unique_startups || 0}
                    </td>
                    <td className="py-3 pl-2 text-[10px] font-mono text-ink-muted">
                      V{r.funnel?.view || 0} · S{r.funnel?.save || 0} · I
                      {r.funnel?.interest || 0} · D{r.funnel?.deal_room || 0} ·
                      R{r.funnel?.rejection || 0}
                    </td>
                    <td className="py-3 text-right">
                      {driftPill(r.overall_drift)}
                    </td>
                    <td className="py-3 pr-2 text-right">
                      <ChevronRight size={16} className="inline text-ink-muted" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {openInvestor && (
        <DetailDrawer
          investorId={openInvestor}
          onClose={() => setOpenInvestor(null)}
        />
      )}
    </DashboardShell>
  );
}
